/**
 * useVideoComposition Hook
 *
 * Manages communication between React and the video-composition.html iframe
 * via postMessage protocol.
 *
 * Features:
 * - Send commands (play, pause, seek, goToSlide)
 * - Receive state updates (currentTime, currentSlide, isPlaying)
 * - Handle events (slideStarted, videoEnded, error)
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { VideoManifest, VideoPlaybackState } from '@/types/videoManifest';
import { getImpersonationUserId } from '@/stores/impersonationStore';
import { useVideoWorkspaceStore } from '@/stores/videoWorkspaceStore';

export interface CompositionEvent {
  type: string;
  slideId?: string;
  fromSlide?: string;
  toSlide?: string;
  message?: string;
}

export interface UseVideoCompositionOptions {
  /** Callback when playback state changes */
  onStateChange?: (state: VideoPlaybackState) => void;
  /** Callback for composition events */
  onEvent?: (event: CompositionEvent) => void;
  /** Callback when manifest is updated (durations corrected) */
  onManifestUpdate?: (manifest: VideoManifest) => void;
  /** Callback when user activity is detected inside the iframe (mouse/touch) */
  onUserActivity?: (activityType?: string) => void;
  /** Origin to accept messages from (default: '*') */
  origin?: string;
  /**
   * Explicit auth token for content fetches (slide jsonPaths + commands). When set,
   * it takes precedence over localStorage 'bearer_token'. Public share/embed pages
   * pass the short-lived video-share session token here.
   */
  authToken?: string;
}

/** Background music configuration for the composition */
export interface BackgroundMusicConfig {
  url: string;
  volume: number;  // 0-1
  loop: boolean;
}

/** Options for loading a manifest */
export interface LoadManifestOptions {
  /** Slide ID to load initially (skips loading slide 0 if different) */
  initialSlideId?: string;
}

export interface UseVideoCompositionReturn {
  /** Ref to attach to the iframe */
  iframeRef: React.RefObject<HTMLIFrameElement | null>;
  /** Current playback state */
  state: VideoPlaybackState;
  /** Whether the composition is ready */
  isReady: boolean;
  /** Load a manifest into the composition */
  loadManifest: (manifest: VideoManifest, options?: LoadManifestOptions) => void;
  /** Start playback */
  play: () => void;
  /** Pause playback */
  pause: () => void;
  /** Seek to a specific time */
  seek: (time: number, options?: { pauseAfterSeek?: boolean }) => void;
  /** Seek addressed in DISPLAY (narration-clock) time — the engine maps it to raw. */
  seekDisplay: (time: number, options?: { pauseAfterSeek?: boolean }) => void;
  /** Live scrub during user drag — cheap, no slide fetches or audio re-init. Pair with scrubEnd. */
  scrub: (time: number) => void;
  /** Commit a scrub (loads target slide if needed, syncs audio, auto-resumes if was playing) */
  scrubEnd: () => void;
  /** Go to a specific slide */
  goToSlide: (slideId: string) => void;
  /** Go to a specific slide and show its end frame (for preview) */
  goToSlidePreview: (slideId: string) => void;
  /** Navigate to a slide with animated transition (for slides presentation mode) */
  navigateSlideWithTransition: (slideId: string, direction: 'next' | 'prev') => void;
  /** Set playback rate */
  setPlaybackRate: (rate: number) => void;
  /** Invalidate a cached slide (after editing) */
  invalidateSlide: (slideId: string) => void;
  /** Request current state */
  requestState: () => void;
  /** Set background music (video-level) */
  setBackgroundMusic: (config: BackgroundMusicConfig) => void;
  /** Update background music volume (0-1) */
  setBackgroundMusicVolume: (volume: number) => void;
  /** Remove background music */
  removeBackgroundMusic: () => void;
  /** Set narrator volume for all slides (0-1) */
  setNarratorVolume: (volume: number) => void;
}

const INITIAL_STATE: VideoPlaybackState = {
  isPlaying: false,
  currentTime: 0,
  totalDuration: 0,
  currentSlideId: '',
  currentSlideIndex: -1,
  currentSlideTime: 0,
  currentSlideDuration: 0,
  playbackRate: 1,
  isContinuousMode: true,
  isTransitioning: false,
};

export function useVideoComposition(
  options: UseVideoCompositionOptions = {}
): UseVideoCompositionReturn {
  const { onStateChange, onEvent, onManifestUpdate, onUserActivity, origin = '*', authToken } = options;

  // Kept in a ref so sendCommand/loadManifest always read the latest token without
  // adding it to their useCallback deps (avoids needless reloads).
  const authTokenRef = useRef<string | undefined>(undefined);
  authTokenRef.current = authToken;

  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [state, setState] = useState<VideoPlaybackState>(INITIAL_STATE);
  const [isReady, setIsReady] = useState(false);

  // Debounce tracking for goToSlidePreview to prevent race conditions
  // when rapidly clicking slides in the filmstrip
  const slidePreviewTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingSlidePreviewRef = useRef<string | null>(null);

  // Send a command to the composition
  const sendCommand = useCallback((action: string, payload?: Record<string, unknown>) => {
    const iframe = iframeRef.current;
    if (!iframe?.contentWindow) {
      console.warn('[useVideoComposition] Iframe not ready');
      return;
    }

    const message = {
      source: 'react-parent',
      action,
      // Always include a fresh auth token so the iframe can replace stale tokens.
      // An explicit authToken (share/embed session) takes precedence over localStorage.
      authToken: authTokenRef.current || localStorage.getItem('bearer_token') || undefined,
      ...payload,
    };

    iframe.contentWindow.postMessage(message, origin);
  }, [origin]);

  // Synchronous direct call into the same-origin engine iframe (E1). Gesture-
  // bearing commands MUST reach controller.play() INSIDE the user-gesture call
  // stack — Safari/iOS accept then silently re-pause a play() issued after the
  // async postMessage hop (R1). window.__ivyController is exposed by
  // video-composition.html before its ready-signal. Returns true if the direct
  // call was made; false → the caller falls back to postMessage (iframe not ready).
  const invokeControllerDirect = useCallback((method: string, ...args: unknown[]): boolean => {
    const controller = (iframeRef.current?.contentWindow as any)?.__ivyController;
    if (!controller || typeof controller[method] !== 'function') return false;
    try {
      // Stamp user intent for analytics parity with the postMessage path.
      const USER_SOURCE: Record<string, string> = { play: 'user_play', pause: 'user_pause', seek: 'user_seek', seekDisplay: 'user_seek' };
      if (USER_SOURCE[method] && typeof controller.setChangeSource === 'function') {
        controller.setChangeSource(USER_SOURCE[method]);
      }
      controller[method](...args);
      return true;
    } catch (e) {
      console.warn('[useVideoComposition] direct controller call failed, falling back to postMessage:', e);
      return false;
    }
  }, []);

  // Command methods
  const loadManifest = useCallback((manifest: VideoManifest, options?: LoadManifestOptions) => {
    // Inject auth token and impersonation user ID into each slide's jsonPath for iframe fetch
    const token = authTokenRef.current || localStorage.getItem('bearer_token');
    const impersonateUserId = getImpersonationUserId();
    const manifestWithAuth: VideoManifest = {
      ...manifest,
      slides: manifest.slides.map(slide => ({
        ...slide,
        // Add token and impersonate as query parameters if jsonPath exists
        // IMPORTANT: Strip any existing token/impersonate first to prevent duplication
        jsonPath: slide.jsonPath && token
          ? (() => {
              // Parse the URL and remove any existing token/impersonate parameters
              const [basePath, queryString] = slide.jsonPath.split('?');
              const params = new URLSearchParams(queryString || '');
              params.delete('token'); // Remove existing token
              params.delete('impersonate'); // Remove existing impersonate
              params.set('token', token);
              if (impersonateUserId) {
                params.set('impersonate', impersonateUserId);
              }
              return `${basePath}?${params.toString()}`;
            })()
          : slide.jsonPath,
      })),
    };

    console.log('[useVideoComposition] Loading manifest:', {
      slideCount: manifestWithAuth.slides.length,
      totalDuration: manifestWithAuth.metadata.totalDuration,
      firstSlideJsonPath: manifestWithAuth.slides[0]?.jsonPath?.substring(0, 100) + '...' || 'none',
      initialSlideId: options?.initialSlideId || 'none (will use slide 0)',
    });

    // Publish each slide's server cache pin (the ?v=..&aa=.. query on the
    // manifest's jsonPath) into the store so panel fetches can pin their own
    // /serve requests and hit the immutable browser cache instead of a full
    // no-cache round trip. The pin rotates on every edit and audio apply.
    const { setServePinForSlide } = useVideoWorkspaceStore.getState();
    for (const slide of manifest.slides) {
      const m = slide.jsonPath?.match(/\/conversations\/[^/]+\/slides\/(slide-\d+)\/serve\?(.+)$/);
      if (m && slide.childConversationId) {
        const params = new URLSearchParams(m[2]);
        const pinParts: string[] = [];
        for (const key of ['v', 'aa'] as const) {
          const val = params.get(key);
          if (val) pinParts.push(`${key}=${val}`);
        }
        if (pinParts.length) {
          setServePinForSlide(slide.childConversationId, m[1], pinParts.join('&'));
        }
      }
    }

    sendCommand('loadManifest', { manifest: manifestWithAuth, initialSlideId: options?.initialSlideId });
  }, [sendCommand]);

  const play = useCallback(() => {
    // Synchronous first (Safari gesture chain); postMessage only if iframe not ready.
    if (!invokeControllerDirect('play')) sendCommand('play');
  }, [invokeControllerDirect, sendCommand]);

  const pause = useCallback(() => {
    if (!invokeControllerDirect('pause')) sendCommand('pause');
  }, [invokeControllerDirect, sendCommand]);

  const seek = useCallback((time: number, options?: { pauseAfterSeek?: boolean }) => {
    const pauseAfterSeek = options?.pauseAfterSeek || false;
    // A seek that RESUMES playback is gesture-bearing (Safari needs the sync
    // stack for the resulting play()); a pause-after-seek has no play() to
    // protect, so postMessage is fine there.
    if (!pauseAfterSeek && invokeControllerDirect('seek', time, { pauseAfterSeek })) return;
    sendCommand('seek', { time, pauseAfterSeek });
  }, [invokeControllerDirect, sendCommand]);

  /**
   * Seek addressed in DISPLAY (narration-clock) time. Chrome that renders the
   * display clock must also seek through it — the engine owns the display↔raw
   * mapping (gate windows, hold-ceiling padding), so a click on the display
   * timeline lands exactly where the playhead shows it. Falls back to a raw
   * seek on engines without the display clock.
   */
  const seekDisplay = useCallback((time: number, options?: { pauseAfterSeek?: boolean }) => {
    const pauseAfterSeek = options?.pauseAfterSeek || false;
    if (!pauseAfterSeek && invokeControllerDirect('seekDisplay', time, { pauseAfterSeek })) return;
    sendCommand('seekDisplay', { time, pauseAfterSeek });
  }, [invokeControllerDirect, sendCommand]);

  const scrub = useCallback((time: number) => {
    sendCommand('scrub', { time });
  }, [sendCommand]);

  const scrubEnd = useCallback(() => {
    sendCommand('scrubEnd');
  }, [sendCommand]);

  const goToSlide = useCallback((slideId: string) => {
    sendCommand('goToSlide', { slideId });
  }, [sendCommand]);

  const goToSlidePreview = useCallback((slideId: string) => {
    // Debounce rapid slide preview requests (100ms trailing debounce)
    // This prevents race conditions when rapidly clicking slides
    pendingSlidePreviewRef.current = slideId;

    // Clear any pending timeout
    if (slidePreviewTimeoutRef.current) {
      clearTimeout(slidePreviewTimeoutRef.current);
    }

    // Set new timeout - only the last request in a rapid sequence will execute
    slidePreviewTimeoutRef.current = setTimeout(() => {
      const targetSlideId = pendingSlidePreviewRef.current;
      if (targetSlideId) {
        console.log('[useVideoComposition] goToSlidePreview (debounced):', targetSlideId);
        sendCommand('goToSlidePreview', { slideId: targetSlideId });
        pendingSlidePreviewRef.current = null;
      }
      slidePreviewTimeoutRef.current = null;
    }, 100);
  }, [sendCommand]);

  const navigateSlideWithTransition = useCallback((slideId: string, direction: 'next' | 'prev') => {
    sendCommand('navigateSlideWithTransition', { slideId, direction });
  }, [sendCommand]);

  const setPlaybackRate = useCallback((rate: number) => {
    sendCommand('setPlaybackRate', { rate });
  }, [sendCommand]);

  const invalidateSlide = useCallback((slideId: string) => {
    sendCommand('invalidateSlide', { slideId });
  }, [sendCommand]);

  const requestState = useCallback(() => {
    sendCommand('getState');
  }, [sendCommand]);

  // Background Music Commands
  const setBackgroundMusic = useCallback((config: BackgroundMusicConfig) => {
    console.log('[useVideoComposition] Setting background music:', config.url);
    sendCommand('setBackgroundMusic', { config });
  }, [sendCommand]);

  const setBackgroundMusicVolume = useCallback((volume: number) => {
    console.log('[useVideoComposition] Setting background music volume:', volume);
    sendCommand('setBackgroundMusicVolume', { volume: Math.max(0, Math.min(1, volume)) });
  }, [sendCommand]);

  const removeBackgroundMusic = useCallback(() => {
    console.log('[useVideoComposition] Removing background music');
    sendCommand('removeBackgroundMusic');
  }, [sendCommand]);

  // Narrator Volume Command
  const setNarratorVolume = useCallback((volume: number) => {
    console.log('[useVideoComposition] Setting narrator volume:', volume);
    sendCommand('setNarratorVolume', { volume: Math.max(0, Math.min(1, volume)) });
  }, [sendCommand]);

  // Listen for messages from the composition
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      const data = event.data;

      // Ignore non-composition messages
      if (!data || data.source !== 'video-composition') return;

      // Handle user activity from iframe (mouse/touch) — used to show controls
      if (data.type === 'userActivity') {
        onUserActivity?.(data.activityType);
        return;
      }

      // Handle state updates
      if (data.type === 'stateUpdate' && data.state) {
        const newState = data.state as VideoPlaybackState;
        setState(newState);
        onStateChange?.(newState);

        // Mark as ready on first state update
        if (!isReady) {
          setIsReady(true);
        }
      }

      // Handle manifest updates (durations corrected after loading slides)
      if (data.type === 'manifestUpdate' && data.manifest) {
        console.log('[useVideoComposition] Manifest updated:', {
          slideCount: data.manifest.slides?.length,
          totalDuration: data.manifest.metadata?.totalDuration
        });
        onManifestUpdate?.(data.manifest as VideoManifest);
      }

      // Handle events
      if (data.type && data.type !== 'stateUpdate') {
        const compositionEvent: CompositionEvent = {
          type: data.type,
          slideId: data.slideId,
          fromSlide: data.fromSlide,
          toSlide: data.toSlide,
          message: data.message,
        };

        onEvent?.(compositionEvent);

        // Handle specific events
        switch (data.type) {
          case 'manifestLoaded':
            console.log('[useVideoComposition] Manifest loaded successfully');
            setIsReady(true);
            break;
          case 'videoEnded':
            console.log('[useVideoComposition] Video ended');
            break;
          case 'error':
            console.error('[useVideoComposition] Error from composition:', data.message);
            break;
          default:
            console.log('[useVideoComposition] Event:', data.type, data);
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [isReady, onStateChange, onEvent, onManifestUpdate, onUserActivity]);

  // Reset state when iframe changes
  // IMPORTANT: The iframe load event may fire before this effect attaches the listener
  // (e.g. when video-composition.html is served from browser cache). We handle this by:
  // 1. Checking if iframe is already loaded when the effect runs
  // 2. Having the iframe proactively broadcastState() on init (see video-composition.html)
  // 3. Polling as a safety net if isReady is still false after 2 seconds
  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    const handleLoad = () => {
      console.log('[useVideoComposition] Iframe loaded');
      // Request initial state after a short delay
      setTimeout(requestState, 100);
    };

    iframe.addEventListener('load', handleLoad);

    // If iframe is already loaded (cached), the load event was missed — trigger manually
    try {
      if (iframe.contentDocument?.readyState === 'complete') {
        console.log('[useVideoComposition] Iframe already loaded (cached), requesting state');
        setTimeout(requestState, 100);
      }
    } catch (_e) {
      // Cross-origin — can't check readyState, rely on load event + iframe broadcastState
    }

    return () => iframe.removeEventListener('load', handleLoad);
  }, [requestState]);

  // Safety-net: if isReady is still false after 2s, retry requestState every second
  // This handles edge cases where both the load event and the proactive broadcastState were missed
  useEffect(() => {
    if (isReady) return;

    const retryTimer = setInterval(() => {
      console.log('[useVideoComposition] Retrying requestState (isReady still false)');
      requestState();
    }, 2000);

    // Give up after 10s to avoid infinite polling
    const giveUpTimer = setTimeout(() => {
      clearInterval(retryTimer);
    }, 10000);

    return () => {
      clearInterval(retryTimer);
      clearTimeout(giveUpTimer);
    };
  }, [isReady, requestState]);

  // Cleanup debounce timeout on unmount
  useEffect(() => {
    return () => {
      if (slidePreviewTimeoutRef.current) {
        clearTimeout(slidePreviewTimeoutRef.current);
      }
    };
  }, []);

  return {
    iframeRef,
    state,
    isReady,
    loadManifest,
    play,
    pause,
    seek,
    seekDisplay,
    scrub,
    scrubEnd,
    goToSlide,
    goToSlidePreview,
    navigateSlideWithTransition,
    setPlaybackRate,
    invalidateSlide,
    requestState,
    // Background music
    setBackgroundMusic,
    setBackgroundMusicVolume,
    removeBackgroundMusic,
    setNarratorVolume,
  };
}
